import { create } from 'zustand';
import { vaultApi } from '../api/vaultApi';
import type { BonusPack } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

interface AdminBonusesState {
  bonuses: BonusPack[];
  load: LoadState;
  loadingIds: string[];

  loadBonuses: () => Promise<void>;
  createBonus: (bonus: BonusPack) => Promise<BonusPack>;
  updateBonus: (id: string, patch: Partial<BonusPack>) => Promise<BonusPack>;
  deleteBonus: (id: string) => Promise<void>;
  reset: () => void;
}

export const useAdminBonusesStore = create<AdminBonusesState>((set) => ({
  bonuses: [],
  load: IDLE_LOAD,
  loadingIds: [],

  async loadBonuses() {
    set({ load: LOADING_LOAD });
    try {
      const bonuses = await vaultApi.getAdminBonuses();
      set({ bonuses, load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
    }
  },

  async createBonus(bonus) {
    const created = await vaultApi.createAdminBonus(bonus);
    set((s) => ({ bonuses: [created, ...s.bonuses.filter((candidate) => candidate.id !== created.id)] }));
    return created;
  },

  async updateBonus(id, patch) {
    set((s) => ({ loadingIds: [...s.loadingIds, id] }));
    try {
      const updated = await vaultApi.updateAdminBonus(id, patch);
      set((s) => ({
        bonuses: s.bonuses.map((candidate) => (candidate.id === id ? updated : candidate)),
        loadingIds: s.loadingIds.filter((candidate) => candidate !== id),
      }));
      return updated;
    } catch (err) {
      set((s) => ({ loadingIds: s.loadingIds.filter((candidate) => candidate !== id) }));
      throw err;
    }
  },

  async deleteBonus(id) {
    set((s) => ({ loadingIds: [...s.loadingIds, id] }));
    try {
      await vaultApi.deleteAdminBonus(id);
      set((s) => ({
        bonuses: s.bonuses.filter((candidate) => candidate.id !== id),
        loadingIds: s.loadingIds.filter((candidate) => candidate !== id),
      }));
    } catch (err) {
      set((s) => ({ loadingIds: s.loadingIds.filter((candidate) => candidate !== id) }));
      throw err;
    }
  },

  reset() {
    set({ bonuses: [], load: IDLE_LOAD, loadingIds: [] });
  },
}));
